import { useEffect, useMemo, useState } from "react";
import { getPlaceDataSubmissionsForPlace } from "../../services/placeService";
import { PlaceDataSubmission } from "../../types/Place";
import { PlaceDataSubmissionStatus } from "../../types/PlaceDataSubmissionStatus";
import { debugLogError } from "../../utils/debug-log-error";
import "./DataSubmissionStatusSummary.scss";

interface DataSubmissionStatusSummaryProps {
  placeId: string;
}

const DataSubmissionStatusSummary: React.FC<
  DataSubmissionStatusSummaryProps
> = ({ placeId }) => {
  const [submissions, setSubmissions] = useState<PlaceDataSubmission[]>([]);

  useEffect(() => {
    getPlaceDataSubmissionsForPlace(placeId)
      .then((submissions) => {
        setSubmissions(submissions);
      })
      .catch((error) => {
        debugLogError("DataSubmissionStatusSummary", error);
      });
  }, [placeId]);

  const summaryText = useMemo(() => {
    const submissionsByStatus: Record<
      PlaceDataSubmissionStatus,
      PlaceDataSubmission[]
    > = {
      [PlaceDataSubmissionStatus.ACCEPTED]: [],
      [PlaceDataSubmissionStatus.REJECTED]: [],
      [PlaceDataSubmissionStatus.PENDING]: [],
      [PlaceDataSubmissionStatus.UNDER_REVIEW]: [],
      [PlaceDataSubmissionStatus.DUPLICATE]: [],
      [PlaceDataSubmissionStatus.UNKNOWN]: [],
    };
    submissions.forEach((submission) => {
      submissionsByStatus[
        submission.submissionStatus ?? PlaceDataSubmissionStatus.UNKNOWN
      ].push(submission);
    });
    const acceptedSubmissions =
      submissionsByStatus[PlaceDataSubmissionStatus.ACCEPTED].length;

    const pendingSubmissions =
      submissionsByStatus[PlaceDataSubmissionStatus.PENDING].length;
    const underReviewSubmissions =
      submissionsByStatus[PlaceDataSubmissionStatus.UNDER_REVIEW].length;
    const unknownSubmissions =
      submissionsByStatus[PlaceDataSubmissionStatus.UNKNOWN].length;
    const otherSubmssions =
      pendingSubmissions + underReviewSubmissions + unknownSubmissions;

    const summaryParts: string[] = [];
    if (acceptedSubmissions > 0) {
      const word = acceptedSubmissions === 1 ? "contribution" : "contributions";
      summaryParts.push(`${acceptedSubmissions} community ${word} accepted`);
    }
    if (otherSubmssions > 0) {
      const word = otherSubmssions === 1 ? "contribution" : "contributions";
      summaryParts.push(
        `${otherSubmssions} community ${word} pending / under review`
      );
    }
    return summaryParts;
  }, [submissions]);

  return (
    <div className="data-submission-status-summary">
      {summaryText.map((t, i) => (
        <div key={`${i}-summary`} className="summary-part">
          {t}
        </div>
      ))}
    </div>
  );
};

export default DataSubmissionStatusSummary;
